import { getEnvVar } from '../envUtils';
import ApiService from './ApiService';

const BASE = () => getEnvVar('REACT_APP_BACKEND_BASE_URL');

// ─── Setup APIs ───────────────────────────────────────────────────────────────

/**
 * GET /v1/mission/{missionId}/validateCandidateSatelliteGroups
 */
const getValidateCandidateSatelliteGroups = (missionId: string) =>
  ApiService.get(
    `${BASE()}v1/mission/${missionId}/validateCandidateSatelliteGroups`,
  );

const getSetupsBySatellite = (missionId: string, satelliteGroupId: string, satelliteId: string) =>
  ApiService.get(
    `${BASE()}v1/mission/${missionId}/validate/satelliteGroups/${satelliteGroupId}/satellites/${satelliteId}/setups`,
  );

type ValidationSessionsFilters = {
  satelliteGroupId?: string;
  satelliteId?: string;
  setupId?: string;
  from?: number;
  to?: number;
  status?: string;
};

const getValidationSessions = (missionId: string, filters: ValidationSessionsFilters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    params.set(key, String(value));
  });
  const query = params.toString();
  return ApiService.get(
    `${BASE()}test/v1/mission/${missionId}/validate/sessions${query ? `?${query}` : ''}`,
  );
};

const createSetup = (
  missionId: string,
  satelliteGroupId: string,
  satelliteId: string,
  payload: Record<string, unknown>,
) =>
  ApiService.post(
    `${BASE()}v1/mission/${missionId}/validate/satelliteGroups/${satelliteGroupId}/satellites/${satelliteId}/setups`,
    payload,
  );

const deleteSetup = (
  missionId: string,
  setupId: string,
  satelliteGroupId: string,
  satelliteId: string,
) =>
  ApiService.delete(
    `${BASE()}v1/mission/${missionId}/validate/satelliteGroups/${satelliteGroupId}/satellites/${satelliteId}/setups/${setupId}`,
  );

const getAvailableSubsystems = (missionId: string) =>
  ApiService.get(
    `${BASE()}v1/mission/${missionId}/validate/subsystems`,
  );

const getSetupSubsystemDiagram = (
  missionId: string,
  satelliteGroupId: string,
  satelliteId: string,
  setupId: string,
) =>
  ApiService.get(
    `${BASE()}v1/mission/${missionId}/validate/satelliteGroups/${satelliteGroupId}/satellites/${satelliteId}/setups/${setupId}/subsystems`,
  );

/**
 * DELETE /test/v1/mission/{missionId}/scenario/{scenarioId}/undeploy/validate
 */
const undeployValidate = (missionId: string, scenarioId: string) =>
  ApiService.delete(
    `${BASE()}test/v1/mission/${missionId}/scenario/${scenarioId}/undeploy/validate`,
  );

// ─── Results APIs (Validate command/testcase) ─────────────────────────────────

/**
 * PATCH /groundstation/comms/command/{commandId}
 * Body: { Result: "pass" | "fail" | "timeout" }
 */
const patchTelecommandResult = (commandId: string, result: "pass" | "fail" | "timeout") =>
  ApiService.patch(`${BASE()}groundstation/comms/command/${commandId}`, {
    Result: result === "pass" ? "Pass" : result === "timeout" ? "Timeout" : "Fail",
  });

/**
 * PATCH /groundstation/comms/{testCaseId}/command
 * Body: { Result: "pass" | "fail" | "timeout" }
 */
const patchTestCaseResult = (testCaseId: string, result: "pass" | "fail" | "timeout") =>
  ApiService.patch(`${BASE()}groundstation/comms/${testCaseId}/command`, {
    Result: result === "pass" ? "Pass" : result === "timeout" ? "Timeout" : "Fail",
  });

// ─── Test APIs (Console Test tab) ─────────────────────────────────────────────
// NOTE: getAllTestCases and getTestCases use project/v1 base path — verify with backend
//       if these should use test/v1 instead (comments above previously said test/v1).

// ─── Scenario/session delete (reuse simulate endpoint) ────────────────────────

/**
 * DELETE /test/{missionId}/scenario/{scenarioId}
 * (Reused for Validate "session" delete as per current backend contract.)
 */
const deleteScenario = (missionId: string, scenarioId: string) =>
  ApiService.delete(`${BASE()}test/${missionId}/scenario/${scenarioId}`);

const getAllTestCases = (missionId: string, satelliteId: string) =>
  ApiService.get(
    `${BASE()}v1/mission/${missionId}/validate/testCases?satelliteId=${satelliteId}`,
  );

const getTestCaseById = (missionId: string, testCaseId: string, satelliteId: string) =>
  ApiService.get(
    `${BASE()}v1/mission/${missionId}/validate/testCase/${testCaseId}?satelliteId=${satelliteId}`,
  );

const getTestCases = (missionId: string, testPlanId: string) =>
  ApiService.get(
    `${BASE()}v1/mission/${missionId}/validate/testPlans/${testPlanId}/testCases`,
  );

const executeTestCase = (
  missionId: string,
  testCaseId: string,
  scenarioId: string,
  payload: Record<string, unknown>,
) =>
  ApiService.post(
    `${BASE()}test/v1/mission/${missionId}/validate/testCases/${testCaseId}/sessions/${scenarioId}/execute`,
    payload,
  );

const executeTestPlan = (
  missionId: string,
  testPlanId: string,
  scenarioId: string,
  payload: Record<string, unknown>,
) =>
  ApiService.post(
    `${BASE()}test/v1/mission/${missionId}/validate/testPlans/${testPlanId}/sessions/${scenarioId}/execute`,
    payload,
  );

/**
 * POST /test/v1/mission/{missionId}/satelliteGroup/{satelliteGroupId}/satellite/{satelliteId}/deploy/validate
 * Body: { name, testingEnvironment, setupId, teams, setupSanityTestPlanId }
 */
const deployValidate = (
  missionId: string,
  satelliteGroupId: string,
  satelliteId: string,
  body: { name: string; testingEnvironment: string; setupId: string; teams: string[]; setupSanityTestPlanId?: string },
) =>
  ApiService.post(
    `${BASE()}test/v1/mission/${missionId}/satelliteGroup/${satelliteGroupId}/satellite/${satelliteId}/deploy/validate`,
    body,
  );

/**
 * GET /test/v1/validate/additionalSetupConfigDetails
 */
const getAdditionalSetupConfigDetails = () =>
  ApiService.get(`${BASE()}test/v1/validate/additionalSetupConfigDetails`);

/**
 * GET /test/v1/validate/additionalSessionConfigDetails
 */
const getAdditionalSessionConfigDetails = () =>
  ApiService.get(`${BASE()}test/v1/validate/additionalSessionConfigDetails`);

/**
 * GET /test/v1/mission/{missionId}/validate/sessions/{scenarioId}/result
 */
const getSessionResult = (missionId: string, scenarioId: string) =>
  ApiService.get(
    `${BASE()}test/v1/mission/${missionId}/validate/sessions/${scenarioId}/result`,
  );

const getSessionSubsystemDiagram = (missionId: string, scenarioId: string) =>
  ApiService.get(
    `${BASE()}test/v1/mission/${missionId}/validate/sessions/${scenarioId}/subsystems`,
  );

const runSessionSanityTest = (
  missionId: string,
  scenarioId: string,
  sanityTestPlanId: string,
) =>
  ApiService.post(
    `${BASE()}test/v1/mission/${missionId}/validate/sessions/${scenarioId}/sanity/run`,
    { sanityTestPlanId },
  );

/**
 * GET /test/v1/mission/{missionId}/validate/sessions/{scenarioId}/report/pdf/download-url?mode={mode}
 * Session-level — exports all test plans for that session (used by "Export All Reports")
 */
const getSessionReportPdfDownloadToken = (
  missionId: string,
  scenarioId: string,
  mode: 'executive' | 'complete',
) =>
  ApiService.get(
    `${BASE()}test/v1/mission/${missionId}/validate/sessions/${scenarioId}/report/pdf/download-url?mode=${mode}`,
  );

/**
 * GET /test/v1/mission/{missionId}/validate/sessions/{scenarioId}/report/excel/download-url
 * Session-level — exports all test plans for that session (used by "Export All Reports")
 */
const getSessionReportExcelDownloadToken = (
  missionId: string,
  scenarioId: string,
) =>
  ApiService.get(
    `${BASE()}test/v1/mission/${missionId}/validate/sessions/${scenarioId}/report/excel/download-url`,
  );

/**
 * GET /test/v1/mission/{missionId}/validate/sessions/{scenarioId}/tests/{testId}/report/pdf/download-url?mode={mode}
 * Test-level — used by Reports tab download button (testId = selected testPlanId or testCaseId)
 */
const getTestReportPdfDownloadToken = (
  missionId: string,
  scenarioId: string,
  testId: string,
  mode: 'executive' | 'complete',
) =>
  ApiService.get(
    `${BASE()}test/v1/mission/${missionId}/validate/sessions/${scenarioId}/tests/${testId}/report/pdf/download-url?mode=${mode}`,
  );

/**
 * GET /test/v1/mission/{missionId}/validate/sessions/{scenarioId}/tests/{testId}/report/excel/download-url
 * Test-level — used by Reports tab download button (testId = selected testPlanId or testCaseId)
 */
const getTestReportExcelDownloadToken = (
  missionId: string,
  scenarioId: string,
  testId: string,
) =>
  ApiService.get(
    `${BASE()}test/v1/mission/${missionId}/validate/sessions/${scenarioId}/tests/${testId}/report/excel/download-url`,
  );

// ─── Test Plans ───────────────────────────────────────────────────────────────

/**
 * GET /v1/mission/{missionId}/validate/testPlans?satelliteId={satelliteId}
 */
const getAllTestPlans = (missionId: string, satelliteId: string) =>
  ApiService.get(
    `${BASE()}v1/mission/${missionId}/validate/testPlans?satelliteId=${satelliteId}`,
  );

/**
 * Hydrates one selected plan with its nested test cases and validations.
 * The list endpoint remains the source for lightweight list/dropdown data.
 */
const getTestPlanById = (missionId: string, testPlanId: string) =>
  ApiService.get(
    `${BASE()}v1/mission/${missionId}/validate/testPlans/${encodeURIComponent(testPlanId)}`,
  );

/**
 * GET /v1/mission/{missionId}/satellitegroup/{satelliteGroupId}/validation-configs?mode=sat_group_minimal
 */
const getValidationConfigs = (missionId: string, satelliteGroupId: string) =>
  ApiService.get(
    `${BASE()}v1/mission/${missionId}/satellitegroup/${satelliteGroupId}/validation-configs?mode=sat_group_minimal`,
  );

/**
 * GET /v1/mission/{missionId}/satellitegroup/{satelliteGroupId}/validation-configs?mode=sat_group_detailed&commandId={commandId}&saId={saId}&daId={daId}
 */
const getValidationConfigDetails = (
  missionId: string,
  satelliteGroupId: string,
  commandId: number,
  saId: number,
  daId: number,
  commandName: string
) =>
  ApiService.get(
    `${BASE()}v1/mission/${missionId}/satellitegroup/${satelliteGroupId}/validation-configs?mode=sat_group_detailed&commandId=${commandId}&saId=${saId}&daId=${daId}&commandName=${commandName}`,
  );

/**
 * POST /v1/mission/{missionId}/satellitegroup/{satelliteGroupId}/validation-configs/import
 */
const importValidationConfigs = (missionId: string, satelliteGroupId: string, formData: FormData) =>
  ApiService.post(
    `${BASE()}v1/mission/${missionId}/satellitegroup/${satelliteGroupId}/validation-configs/import`,
    formData,
  );

/**
 * POST /v1/mission/{missionId}/satellitegroup/{satelliteGroupId}/validation-configs/export
 */
const exportValidationConfigs = (missionId: string, satelliteGroupId: string, ids: string[]) =>
  ApiService.post(
    `${BASE()}v1/mission/${missionId}/satellitegroup/${satelliteGroupId}/validation-configs/export`,
    { ids },
  );

/**
 * PUT /project/v1/mission/{missionId}/satellitegroup/{satelliteGroupId}/validation-configs/{validationConfigId}
 */
const updateValidationConfig = (
  missionId: string,
  satelliteGroupId: string,
  validationConfigId: string,
  payload: Record<string, any>
) =>
  ApiService.put(
    `${BASE()}v1/mission/${missionId}/satellitegroup/${satelliteGroupId}/validation-configs/${validationConfigId}`,
    payload,
  );

/**
 * GET /test/v1/mission/{missionId}/validate/truetwin/{truetwinId}/executionStatus
 */
const getValidationExecutionStatus = (missionId: string, truetwinId: string) =>
  ApiService.get(
    `${BASE()}test/v1/mission/${missionId}/validate/truetwin/${truetwinId}/executionStatus`,
  );

/**
 * POST /test/v1/mission/{missionId}/validate/testCases/{testCaseId}/sessions/{scenarioId}/stop?truetwinId={truetwinId}
 */
const cancelTestCase = (
  missionId: string,
  testCaseId: string,
  scenarioId: string,
  truetwinId: string,
) =>
  ApiService.post(
    `${BASE()}test/v1/mission/${missionId}/validate/testCases/${testCaseId}/sessions/${scenarioId}/stop?truetwinId=${encodeURIComponent(
      truetwinId,
    )}`,
  );

/**
 * POST /test/v1/mission/{missionId}/validate/testPlans/{testPlanId}/sessions/{scenarioId}/stop?truetwinId={truetwinId}
 */
const cancelTestPlan = (
  missionId: string,
  testPlanId: string,
  scenarioId: string,
  truetwinId: string,
) =>
  ApiService.post(
    `${BASE()}test/v1/mission/${missionId}/validate/testPlans/${testPlanId}/sessions/${scenarioId}/stop?truetwinId=${encodeURIComponent(
      truetwinId,
    )}`,
  );

const ValidateService = {
  getValidateCandidateSatelliteGroups,
  getSetupsBySatellite,
  getValidationSessions,
  createSetup,
  deleteSetup,
  getAvailableSubsystems,
  getSetupSubsystemDiagram,
  undeployValidate,
  patchTelecommandResult,
  patchTestCaseResult,
  deleteScenario,
  deployValidate,
  getAllTestCases,
  getTestCaseById,
  getTestCases,
  executeTestCase,
  executeTestPlan,
  getAdditionalSetupConfigDetails,
  getAdditionalSessionConfigDetails,
  getAllTestPlans,
  getValidationConfigs,
  getValidationConfigDetails,
  importValidationConfigs,
  exportValidationConfigs,
  updateValidationConfig,
  getTestPlanById,
  getSessionResult,
  getSessionSubsystemDiagram,
  runSessionSanityTest,
  getTestReportPdfDownloadToken,
  getTestReportExcelDownloadToken,
  getSessionReportPdfDownloadToken,
  getSessionReportExcelDownloadToken,
  getValidationExecutionStatus,
  cancelTestCase,
  cancelTestPlan,
};

export default ValidateService;
